import { Edit, Eye, FileText, Home, Plus, Trash2, Upload } from 'lucide-react';
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import ContractModal from './ContractModal';
import ContractUpload from '../../components/ContractUpload/ContractUpload';

const Contracts = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [modalMode, setModalMode] = useState('add'); // 'add', 'edit', 'view'
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPropertyOwner, setSelectedPropertyOwner] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const queryClient = useQueryClient();

  // 사용자 정보 가져오기 (RMC 사용자 확인용)
  const { data: userInfo } = useQuery(['user-info'], () =>
    api.get('/auth/me'),
    {
      onSuccess: (data) => {
        setCurrentUser(data);
        // RMC 사용자가 일반 경로로 접속한 경우 RMC 전용 경로로 리다이렉트
        if (data?.role === 'rental_management_company') {
          navigate('/rmc/contracts', { replace: true });
        }
      }
    }
  );

  // 임대사업자 목록 가져오기 (RMC 사용자용)
  const { data: propertyOwners } = useQuery(
    ['property-owners', userInfo?.role],
    () => api.get('/property-owners'),
    {
      enabled: userInfo?.role === 'rental_management_company',
      staleTime: 5 * 60 * 1000, // 5분
      cacheTime: 10 * 60 * 1000, // 10분
      select: (data) => {
        // API 응답이 배열인지 확인하고, 아니면 빈 배열 반환
        if (Array.isArray(data)) {
          return data;
        }
        // 에러 응답이나 다른 구조인 경우 빈 배열 반환
        console.warn('propertyOwners API 응답이 배열이 아닙니다:', data);
        return [];
      }
    }
  );

  // 건물 목록 가져오기 (RMC 사용자는 임대사업자별, 임대사업자는 자신의 건물)
  const { data: buildings } = useQuery(
    ['buildings', selectedPropertyOwner, userInfo?.role],
    () => {
      if (userInfo?.role === 'rental_management_company') {
        // RMC 사용자는 선택된 임대사업자의 건물
        const url = selectedPropertyOwner
          ? `/buildings?property_owner_id=${selectedPropertyOwner}`
          : '/buildings';
        return api.get(url);
      } else {
        // 임대사업자 사용자는 자신의 건물
        return api.get('/buildings');
      }
    },
    {
      enabled: !!userInfo,
      select: (data) => {
        // API 응답이 배열인지 확인하고, 아니면 빈 배열 반환
        if (Array.isArray(data)) {
          return data;
        }
        // 에러 응답이나 다른 구조인 경우 빈 배열 반환
        console.warn('buildings API 응답이 배열이 아닙니다:', data);
        return [];
      }
    }
  );

  // 계약 목록 조회 - 응답 처리 단순화
  const { data: contractsData, isLoading, error } = useQuery(
    ['contracts', statusFilter, selectedPropertyOwner, selectedBuilding],
    async () => {
      try {
        const params = {};
        if (statusFilter !== 'all') {
          params.status = statusFilter;
        }
        if (selectedPropertyOwner) {
          params.property_owner_id = selectedPropertyOwner;
        }
        if (selectedBuilding) {
          params.building_id = selectedBuilding;
        }
        console.log('🔍 계약 목록 조회 시작');
        console.log('🔍 params:', params);
        const response = await api.get('/contracts', { params });
        console.log('🔍 계약 목록 API 응답:', response);
        console.log('🔍 응답 타입:', typeof response);
        console.log('🔍 Array.isArray(response):', Array.isArray(response));

        // api.js에서 response.data를 반환하므로 response 자체가 데이터
        if (Array.isArray(response)) {
          console.log('🔍 계약 목록 조회 성공: Array 형태, 개수:', response.length);
          return response;
        }

        // response.data가 배열인 경우
        if (response && response.data && Array.isArray(response.data)) {
          console.log('🔍 계약 목록 조회 성공: response.data 형태, 개수:', response.data.length);
          return response.data;
        }

        // response.success && response.data 구조인 경우
        if (response && response.success && response.data && Array.isArray(response.data)) {
          console.log('🔍 계약 목록 조회 성공: response.success.data 형태, 개수:', response.data.length);
          return response.data;
        }

        console.log('⚠️ 계약 목록 조회 실패: 알 수 없는 응답 구조');
        console.log('⚠️ response:', JSON.stringify(response, null, 2));
        return [];
      } catch (error) {
        console.error('❌ 계약 목록 조회 오류:', error);
        return [];
      }
    },
    {
      onError: (error) => {
        console.error('❌ 계약 목록 조회 오류:', error);
      }
    }
  );

  const contracts = contractsData || [];


  // 계약 종료
  const terminateContractMutation = useMutation(
    (contractId) => api.post(`/contracts/${contractId}/terminate`),
    {
      onSuccess: () => {
        toast.success('계약이 성공적으로 종료되었습니다.');
        queryClient.invalidateQueries('contracts');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '계약 종료에 실패했습니다.');
      }
    }
  );

  const handleTerminateContract = (contractId) => {
    if (window.confirm('정말로 이 계약을 종료하시겠습니까?')) {
      terminateContractMutation.mutate(contractId);
    }
  };

  const handleAddContract = () => {
    setSelectedContract(null);
    setModalMode('add');
    setShowModal(true);
  };

  const handleEditContract = (contract) => {
    setSelectedContract(contract);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleViewContract = (contract) => {
    setSelectedContract(contract);
    setModalMode('view');
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedContract(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    queryClient.invalidateQueries('contracts');
  };

  const handleUploadClick = () => {
    if (!selectedBuilding) {
      toast.error('건물을 먼저 선택해주세요.');
      return;
    }
    setShowUploadModal(true);
  };

  const handleUploadClose = () => {
    setShowUploadModal(false);
  };

  const handleUploadSuccess = () => {
    // 업로드 성공 후 관련 데이터 새로고침
    queryClient.invalidateQueries(['contracts']);
    queryClient.invalidateQueries(['units']);
    queryClient.invalidateQueries(['tenants']);
    queryClient.invalidateQueries(['vacant-units']);
  };

  // 임대사업자 변경 시 건물 선택 초기화
  const handlePropertyOwnerChange = (ownerId) => {
    setSelectedPropertyOwner(ownerId);
    setSelectedBuilding(''); // 건물 선택 초기화
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: '활성', className: 'bg-green-100 text-green-800' },
      expired: { label: '만료', className: 'bg-yellow-100 text-yellow-800' },
      terminated: { label: '종료', className: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          계약 목록을 불러오는데 실패했습니다.
          <br />
          <span className="text-sm text-gray-500 mt-2">
            {error.response?.data?.error || error.message || '알 수 없는 오류가 발생했습니다.'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 페이지 헤더 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">계약 관리</h1>
            <p className="text-gray-600">
              임대 계약을 관리하고 모니터링합니다. 계약 상태, 임차인 정보, 월세 및 보증금을 한눈에 확인할 수 있습니다.
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            <button
              onClick={handleUploadClick}
              className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <Upload className="w-4 h-4 mr-2" />
              엑셀 업로드
            </button>
            <button
              onClick={handleAddContract}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              새 계약 등록
            </button>
          </div>
        </div>
      </div>

      {/* 필터 및 통계 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col space-y-4 mb-4">
          {/* 첫 번째 행: 계약 상태와 통계 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">계약 상태:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">전체 ({contracts.length})</option>
                <option value="active">활성 계약</option>
                <option value="expired">만료된 계약</option>
                <option value="terminated">종료된 계약</option>
              </select>
            </div>
            <div className="text-sm text-gray-600">
              총 <span className="font-semibold">{contracts.length}</span>개의 계약
            </div>
          </div>

          {/* 두 번째 행: 임대사업자와 건물 필터 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 임대사업자 필터 (RMC 사용자용) */}
            {userInfo?.role === 'rental_management_company' && (
              <div className="flex items-center space-x-2">
                <Home className="h-4 w-4 text-gray-400" />
                <label className="text-sm font-medium text-gray-700">임대사업자:</label>
                <select
                  value={selectedPropertyOwner}
                  onChange={(e) => handlePropertyOwnerChange(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">전체 임대사업자</option>
                  {propertyOwners && propertyOwners.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.company_name || owner.name} ({owner.buildings_count}개 건물)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* 건물 필터 (모든 사용자용) */}
            <div className="flex items-center space-x-2">
              <Home className="h-4 w-4 text-gray-400" />
              <label className="text-sm font-medium text-gray-700">건물:</label>
              <select
                value={selectedBuilding}
                onChange={(e) => setSelectedBuilding(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={userInfo?.role === 'rental_management_company' && !selectedPropertyOwner}
              >
                <option value="">전체 건물</option>
                {buildings && buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>


      {/* 계약 목록 테이블 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  계약 정보
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  임대 공간
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  임차인 정보
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  계약 기간
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  월세 및 관리비
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  보증금
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  계약 상태
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contracts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <div className="text-gray-500 text-lg mb-4">등록된 계약이 없습니다.</div>
                    <p className="text-gray-400 text-sm mb-4">
                      새로운 임대 계약을 등록하여 임차인과의 계약을 관리하세요.
                    </p>
                    <button
                      onClick={handleAddContract}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
                    >
                      첫 번째 계약 추가하기
                    </button>
                  </td>
                </tr>
              ) : (
                contracts.map((contract) => (
                  <tr key={contract.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        계약번호: {contract.contract_number}
                      </div>
                      <div className="text-sm text-gray-500">
                        유형: {contract.contract_type === 'monthly' ? '월세' : contract.contract_type === 'jeonse' ? '전세' : contract.contract_type}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {contract.unit?.building_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {contract.unit?.unit_number}호 ({contract.unit?.floor}층, {contract.unit?.area}㎡)
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {contract.tenant?.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        연락처: {contract.tenant?.phone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        시작: {contract.start_date && new Date(contract.start_date).toLocaleDateString('ko-KR')}
                      </div>
                      <div className="text-sm text-gray-500">
                        종료: {contract.end_date && new Date(contract.end_date).toLocaleDateString('ko-KR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        월세: {contract.monthly_rent ? (contract.monthly_rent / 10000).toLocaleString() : 0}만원
                        {contract.monthly_rent_vat_included && (
                          <span className="ml-1 text-xs text-blue-600">(부가세 포함)</span>
                        )}
                        {contract.monthly_rent_vat_exempt && (
                          <span className="ml-1 text-xs text-green-600">(면세)</span>
                        )}
                      </div>
                      {contract.maintenance_fee && (
                        <div className="text-sm text-gray-500">
                          관리비: {contract.maintenance_fee.toLocaleString()}원
                          {contract.maintenance_fee_vat_included && (
                            <span className="ml-1 text-xs text-blue-600">(부가세 포함)</span>
                          )}
                          {contract.maintenance_fee_vat_exempt && (
                            <span className="ml-1 text-xs text-green-600">(면세)</span>
                          )}
                        </div>
                      )}
                      {contract.parking_fee && (
                        <div className="text-sm text-gray-500">
                          주차비: {contract.parking_fee.toLocaleString()}원
                          {contract.parking_fee_vat_included && (
                            <span className="ml-1 text-xs text-blue-600">(부가세 포함)</span>
                          )}
                          {contract.parking_fee_vat_exempt && (
                            <span className="ml-1 text-xs text-green-600">(면세)</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {contract.deposit ? (contract.deposit / 10000).toLocaleString() : 0}만원
                      </div>
                      <div className="text-sm text-gray-500">
                        {contract.contract_type === 'jeonse' ? '전세보증금' : '월세보증금'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(contract.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleViewContract(contract)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="상세보기"
                        >
                          <Eye size={16} />
                        </button>
                        {contract.status === 'active' && (
                          <>
                            <button
                              onClick={() => handleEditContract(contract)}
                              className="text-green-600 hover:text-green-900 p-1"
                              title="수정"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleTerminateContract(contract.id)}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="계약 종료"
                              disabled={terminateContractMutation.isLoading}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <ContractModal
          contract={selectedContract}
          mode={modalMode}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {showUploadModal && (
        <ContractUpload
          onClose={handleUploadClose}
          onSuccess={handleUploadSuccess}
          selectedBuilding={selectedBuilding ? buildings.find(b => b.id === selectedBuilding) : null}
        />
      )}
    </div>
  );
};

export default Contracts;











